/**
 * ContextMenu: one action menu per surface (a board, a list, a thread list), opened
 *   - at the pointer on right click (`contextmenu`),
 *   - under a '...' button (click, Enter or Space),
 *   - from the keyboard on the focused element (Shift+F10 or the ContextMenu key).
 *
 * Usage:
 *
 *   const menu = useContextMenu();
 *   const items = (row: Row): ContextMenuEntry[] => [
 *     { label: 'Open', onSelect: () => open(row) },
 *     'separator',
 *     { heading: 'Move to' },
 *     ...targets.map((s) => ({ label: s.label, onSelect: () => move(row, s) })),
 *     { label: 'Delete site', danger: true, onSelect: () => remove(row) },
 *   ];
 *
 *   <article {...menu.surface(row.id, `Actions for ${row.name}`, () => items(row))}>…</article>
 *   <Button
 *     iconOnly
 *     icon="more"
 *     aria-label={`Actions for ${row.name}`}
 *     aria-expanded={menu.openKey === row.id}
 *     {...menu.trigger(row.id, `Actions for ${row.name}`, () => items(row))}
 *   />
 *   {menu.element}
 *
 * - `surface(key, label, getItems)` returns `{ onContextMenu, onKeyDown }` for any element.
 *   Items are built lazily on open, so thousands of rows cost one closure each.
 * - `trigger(key, label, getItems)` returns `{ onClick, aria-haspopup }` for a button; pressing
 *   it again closes the menu. The button sets its own `aria-label` and
 *   `aria-expanded={menu.openKey === key}`.
 * - `surface` and `trigger` are stable across renders, so memoized rows can take them as props.
 * - `element` is rendered once, inside the same dialog as the surface when there is one. It is
 *   promoted to the top layer with the Popover API, so scroll containers and transforms never
 *   clip it and a modal dialog never makes it inert.
 * - `openKey` is the key of the open menu (or null); `close()` closes it.
 *
 * Entries are the `Menu` shape: `{ label, icon?, onSelect, danger?, disabled?, hint?, checked? }`,
 * `'separator'`, `{ heading }`. Destructive items (`danger`) always end up last, after a
 * separator, in the danger colour. Stray separators and empty headings are dropped.
 *
 * Behaviour: clamped to the viewport (flips left or up near an edge); first item focused;
 * ArrowUp/ArrowDown/Home/End move, Enter/Space select; Escape closes and returns focus;
 * Tab, a click outside, scroll, resize and window blur close.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import type { MenuItem } from './overlays';
import './ContextMenu.css';

export type ContextMenuEntry = MenuItem | 'separator' | { heading: string };

type Anchor = { kind: 'point'; x: number; y: number } | { kind: 'rect'; rect: DOMRect; align: 'start' | 'end' };

interface OpenMenu {
  seq: number;
  key: string;
  label: string;
  items: ContextMenuEntry[];
  anchor: Anchor;
  /** Focus goes back here on Escape and after a selection. */
  returnTo: HTMLElement | null;
  /** The '...' button that opened the menu: pressing it again closes instead of reopening. */
  trigger: HTMLElement | null;
}

export interface ContextMenuApi {
  surface: (
    key: string,
    label: string,
    getItems: () => ContextMenuEntry[],
  ) => { onContextMenu: (e: MouseEvent<HTMLElement>) => void; onKeyDown: (e: KeyboardEvent<HTMLElement>) => void };
  trigger: (key: string, label: string, getItems: () => ContextMenuEntry[]) => { onClick: (e: MouseEvent<HTMLElement>) => void; 'aria-haspopup': 'menu' };
  openKey: string | null;
  close: () => void;
  element: ReactNode;
}

const isItem = (e: ContextMenuEntry): e is MenuItem => e !== 'separator' && !('heading' in e);
const isMenuKey = (e: KeyboardEvent) => (e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu';

/** Safe items first, destructive ones last after a separator; no stray separators or empty headings. */
function arrange(items: ContextMenuEntry[]): ContextMenuEntry[] {
  const danger = items.filter((e) => isItem(e) && e.danger);
  const ordered = danger.length ? [...items.filter((e) => !(isItem(e) && e.danger)), 'separator' as const, ...danger] : items;
  const out: ContextMenuEntry[] = [];
  for (const [i, e] of ordered.entries()) {
    const next = ordered[i + 1];
    if (e === 'separator' && (out.length === 0 || out[out.length - 1] === 'separator')) continue;
    if (e !== 'separator' && !isItem(e) && (next === undefined || !isItem(next))) continue;
    out.push(e);
  }
  while (out[out.length - 1] === 'separator') out.pop();
  return out;
}

export function useContextMenu(): ContextMenuApi {
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const current = useRef<OpenMenu | null>(null);
  const seq = useRef(0);
  const lastKeyOpen = useRef(0);
  const close = useCallback(() => setMenu(null), []);
  useLayoutEffect(() => {
    current.current = menu;
  }, [menu]);

  const open = useCallback((m: Omit<OpenMenu, 'seq' | 'returnTo'>) => {
    const active = document.activeElement;
    seq.current += 1;
    setMenu({ ...m, seq: seq.current, returnTo: active instanceof HTMLElement && active !== document.body ? active : null });
  }, []);

  const surface = useCallback<ContextMenuApi['surface']>(
    (key, label, getItems) => ({
      onContextMenu: (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault();
        // The ContextMenu key fires keydown (handled below) and then contextmenu: open once.
        if (performance.now() - lastKeyOpen.current < 300) return;
        const fromKeyboard = e.clientX === 0 && e.clientY === 0;
        const anchor: Anchor = fromKeyboard
          ? { kind: 'rect', rect: (e.target as HTMLElement).getBoundingClientRect(), align: 'start' }
          : { kind: 'point', x: e.clientX, y: e.clientY };
        open({ key, label, items: getItems(), anchor, trigger: null });
      },
      onKeyDown: (e) => {
        if (e.defaultPrevented || !isMenuKey(e)) return;
        e.preventDefault();
        lastKeyOpen.current = performance.now();
        const rect = (e.target as HTMLElement).getBoundingClientRect();
        open({ key, label, items: getItems(), anchor: { kind: 'rect', rect, align: 'start' }, trigger: null });
      },
    }),
    [open],
  );

  const trigger = useCallback<ContextMenuApi['trigger']>(
    (key, label, getItems) => ({
      'aria-haspopup': 'menu',
      onClick: (e) => {
        e.stopPropagation();
        const button = e.currentTarget;
        if (current.current?.trigger === button) {
          setMenu(null);
          return;
        }
        open({ key, label, items: getItems(), anchor: { kind: 'rect', rect: button.getBoundingClientRect(), align: 'end' }, trigger: button });
      },
    }),
    [open],
  );

  return {
    surface,
    trigger,
    openKey: menu?.key ?? null,
    close,
    element: menu ? <ContextMenuPopup key={menu.seq} menu={menu} onClose={close} /> : null,
  };
}

const EDGE = 8;
const supportsPopover = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

function place(anchor: Anchor, w: number, h: number): { left: number; top: number } {
  const vw = document.documentElement.clientWidth;
  const vh = window.innerHeight;
  let left: number;
  let top: number;
  if (anchor.kind === 'point') {
    left = anchor.x + w > vw - EDGE ? anchor.x - w : anchor.x;
    top = anchor.y + h > vh - EDGE ? anchor.y - h : anchor.y;
  } else {
    const r = anchor.rect;
    left = anchor.align === 'end' ? r.right - w : r.left;
    top = r.bottom + 4 + h > vh - EDGE ? r.top - 4 - h : r.bottom + 4;
  }
  return {
    left: Math.max(EDGE, Math.min(left, vw - w - EDGE)),
    top: Math.max(EDGE, Math.min(top, vh - h - EDGE)),
  };
}

function ContextMenuPopup({ menu, onClose }: { menu: OpenMenu; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const items = arrange(menu.items);

  const dismiss = useCallback(
    (restoreFocus: boolean) => {
      onClose();
      if (restoreFocus && menu.returnTo?.isConnected) menu.returnTo.focus({ preventScroll: true });
    },
    [menu.returnTo, onClose],
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (supportsPopover && !el.matches(':popover-open')) el.showPopover();
    setPos(place(menu.anchor, el.offsetWidth, el.offsetHeight));
  }, [menu.anchor]);

  // Hidden elements take no focus: focus the first item once the menu is placed and visible.
  useEffect(() => {
    if (pos) ref.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
  }, [pos]);

  useEffect(() => {
    const inside = (t: EventTarget | null) => t instanceof Node && Boolean(ref.current?.contains(t));
    const onPointerDown = (e: PointerEvent) => {
      if (inside(e.target) || (menu.trigger && e.target instanceof Node && menu.trigger.contains(e.target))) return;
      dismiss(false);
    };
    // Scroll events arrive on the next frame: one from before the menu opened (a click that
    // scrolled its button into view) must not close it.
    const shownAt = performance.now();
    const onScroll = (e: Event) => {
      if (!inside(e.target) && performance.now() - shownAt > 100) dismiss(false);
    };
    const onLeave = () => dismiss(false);
    // Escape while focus sits outside the menu (inside it, the menu's own handler runs).
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') dismiss(true);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onLeave);
    window.addEventListener('blur', onLeave);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onLeave);
      window.removeEventListener('blur', onLeave);
    };
  }, [dismiss, menu.trigger]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const els = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? []);
    const at = els.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      els[(i + els.length) % els.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at < 0 ? -1 : at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(-1);
    else if (e.key === 'Escape' || e.key === 'Tab' || isMenuKey(e)) {
      e.preventDefault();
      dismiss(true);
    }
  };

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={menu.label}
      popover={supportsPopover ? 'manual' : undefined}
      className="popover ctx-menu"
      style={pos ?? { visibility: 'hidden', left: 0, top: 0 }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) =>
        item === 'separator' ? (
          <div key={`sep-${i}`} className="menu-sep" role="separator" />
        ) : 'heading' in item ? (
          <div key={`h-${i}`} className="menu-heading" role="presentation">
            {item.heading}
          </div>
        ) : (
          <button
            key={`i-${i}`}
            type="button"
            role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
            aria-checked={item.checked}
            className={cx('menu-item', item.danger && 'menu-item-danger')}
            disabled={item.disabled}
            title={item.disabled ? item.hint : undefined}
            onClick={() => {
              dismiss(true);
              item.onSelect?.();
            }}
          >
            {item.icon ? <Icon name={item.icon} /> : null}
            <span className="grow">{item.label}</span>
            {item.checked ? <Icon name="check" /> : null}
            {item.disabled && item.hint ? <span className="xsmall faint">{item.hint}</span> : null}
          </button>
        ),
      )}
    </div>
  );
}
